// src/pages/Invoices.jsx
import React, { useState, useEffect, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../styles/badges.css';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Button } from '../components/ui';
import { IconPlus, IconFileExport, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { showConfirm } from '../utils/confirmUtils';

import InvoiceStats from '../components/invoices/InvoiceStats';
import InvoiceFilters from '../components/invoices/InvoiceFilters';
import InvoicesTable from '../components/invoices/InvoicesTable';
import InvoiceForm from '../components/invoices/InvoiceForm';
import InvoiceViewModal from '../components/invoices/InvoiceViewModal';
import InvoiceLayout from '../components/invoices/InvoiceLayout';

import {
  calculateTotals, validateInvoiceForm,
  prepareInvoiceData, formatDateForInput,
} from '../components/invoices/invoiceUtils';
import { usePermissions } from '../hooks/usePermissions';
import ExportModal from '../components/ExportModal';

import clientsAPI from '../services/clientsAPI';
import invoicesAPI from '../services/invoicesAPI';
import api from '../api/api';

const sanitizeDecimalTyping = (val) => {
  if (val === null || val === undefined) return '';
  return String(val).replace(/[^0-9,.\-]/g, '');
};

const parseDecimalFR = (val) => {
  if (val === null || val === undefined) return 0;
  let s = String(val).trim();
  if (s === '') return 0;
  s = s.replace(/\s/g, '').replace(/,/g, '.');
  const parts = s.split('.');
  if (parts.length > 2) s = parts[0] + '.' + parts.slice(1).join('');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

const Invoices = () => {
  const { currentUser, loading: authLoading, tenantType } = useAuth();
  const isMedical = tenantType === 'cabinet_medical';
  const { activeWorkspace, workspaceStockEnabled } = useWorkspace();

  const [factures, setFactures] = useState([]);
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingFacture, setEditingFacture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewingFacture, setViewingFacture] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [cabinetInfo, setCabinetInfo] = useState(null);

  const [stockItems, setStockItems] = useState([]);

  const loadStockItems = async () => {
    try {
      const res = await api.get('/stock/items', { params: { limit: 200 } });
      if (res.data.ok) setStockItems(res.data.items);
    } catch { setStockItems([]); }
  };

  useEffect(() => { if (workspaceStockEnabled) loadStockItems(); }, [workspaceStockEnabled]);

  const [formData, setFormData] = useState({
    clientId: '', number: '',
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
    items: [{ description: '', qty: 1, price: 0, tax: isMedical ? 7 : 19, itemType: 'honoraire', patientName: '', patientIdNumber: '', stockItemId: null }],
    subtotal: 0, totalTax: 0, timbreFiscal: 1.0, total: 0,
    status: 'sent', notes: '', isGlobal: false,
  });

  const [showExportModal, setShowExportModal] = useState(false);
  const [filtres, setFiltres] = useState({ status: '', clientId: '', dateDebut: '', dateFin: '' });

  const invoiceExportColumns = [
    { key: 'number', label: 'N°', accessor: (f) => f.number },
    { key: 'client', label: 'Client', accessor: (f) => getClientObject(f)?.name || getClientObject(f)?.companyName || f.clientId?.name || 'N/A' },
    { key: 'date', label: 'Date', accessor: (f) => f.date ? new Date(f.date).toLocaleDateString('fr-FR') : '' },
    { key: 'dueDate', label: 'Échéance', accessor: (f) => f.dueDate ? new Date(f.dueDate).toLocaleDateString('fr-FR') : '' },
    { key: 'totalHT', label: 'Total HT', accessor: (f) => (f.totalHT ?? f.subtotal ?? 0).toFixed(2) },
    { key: 'totalTTC', label: 'Total TTC', accessor: (f) => (f.totalTTC ?? f.total ?? 0).toFixed(2) },
    { key: 'status', label: 'Statut', accessor: (f) => ({ draft: 'Brouillon', sent: 'Envoyée', paid: 'Payée', partial: 'Partiel', cancelled: 'Annulée' })[f.status] || f.status },
  ];

  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);

  const printRef = useRef();
  const { canAccessClient, canModifyResource, canDeleteResource, hasPermission } = usePermissions();

  useEffect(() => {
    if (authLoading) return;
    if (!currentUser) return;
    chargerClients();
    chargerFactures();
    chargerCabinetInfo();
  }, [authLoading, currentUser, activeWorkspace]);

  const toastSuccess = (msg) => toast.success(msg, { position: 'top-right', autoClose: 3000 });
  const toastError   = (msg) => toast.error(msg,   { position: 'top-right', autoClose: 5000 });

  const chargerCabinetInfo = async () => {
    try {
      const response = await api.get('/settings');
      const data = response.data;
      setCabinetInfo({
        name: data.companyName || data.name || 'CABINET',
        address: data.address || '', city: data.city || '',
        phone: data.phone || '', email: data.email || '',
        taxId: data.fiscalId || data.taxId || '',
        rib: data.rib || '', bank: data.bank || '',
        paymentTerms: data.paymentTerms || 'Paiement à 30 jours',
        defaultNotes: data.defaultNotes || '',
        logo: data.logo || null, ...data,
      });
    } catch (error) { console.error('Erreur chargement infos cabinet:', error); }
  };

  const chargerClients = async () => {
    try {
      const data = await clientsAPI.findAll();
      setClients(Array.isArray(data) ? data : []);
    } catch (error) { console.error('Erreur chargerClients:', error); setClients([]); }
  };

  const chargerFactures = async () => {
    setLoading(true);
    try {
      const data = await invoicesAPI.findAll();
      setFactures(Array.isArray(data) ? data : []);
    } catch (error) { console.error('Erreur chargerFactures:', error); setFactures([]); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const clientId = formData.clientId;
    if (!validateInvoiceForm(formData, factures, toastError)) return;
    if (!formData.isGlobal) {
      if (!clientId) { toastError('Veuillez sélectionner un client'); return; }
      if (!canAccessClient(clientId)) { toastError("Vous n'avez pas accès à ce client"); return; }
    }
    if (editingFacture && !canModifyResource(editingFacture, 'invoice')) { toastError("Vous n'êtes pas autorisé à modifier cette facture"); return; }
    try {
      const token = localStorage.getItem('token');
      if (!token) { toastError('Session expirée'); return; }
      const factureData = prepareInvoiceData(formData, calculateTotals);
      factureData.isGlobal = formData.isGlobal;
      if (activeWorkspace) factureData.workspaceId = activeWorkspace._id;
      if (editingFacture?._id) await invoicesAPI.update(editingFacture._id, factureData);
      else await invoicesAPI.create(factureData);
      toastSuccess(editingFacture ? 'Facture modifiée avec succès!' : 'Facture créée avec succès!');
      resetForm(); setShowModal(false); await chargerFactures();
    } catch (error) {
      console.error('Erreur réseau:', error);
      toastError(error.response?.data?.error || 'Erreur lors de la sauvegarde.');
    }
  };

  const resetForm = () => {
    setFormData({
      clientId: '', number: '',
      date: new Date().toISOString().split('T')[0],
      dueDate: '',
      items: [{ description: '', qty: 1, price: 0, tax: isMedical ? 7 : 19, itemType: 'honoraire', patientName: '', patientIdNumber: '', stockItemId: null }],
      subtotal: 0, totalTax: 0, timbreFiscal: 1.0, total: 0,
      status: 'sent', notes: cabinetInfo?.defaultNotes || '', isGlobal: false,
    });
    setEditingFacture(null);
  };

  const editerFacture = async (facture) => {
    if (!canModifyResource(facture, 'invoice')) { toastError("Vous n'êtes pas autorisé à modifier cette facture"); return; }
    const dateFormatted    = formatDateForInput(facture.date);
    const dueDateFormatted = formatDateForInput(facture.dueDate);
    const itemsEdit = facture.items?.map((item) => ({
      description: item.description || '',
      qty: parseInt(item.quantity || 1),
      price: parseFloat(item.priceHT || 0),
      tax: typeof item.tvaRate === 'number' ? item.tvaRate : (typeof item.tva === 'number' ? item.tva * 100 : (item.itemType === 'debours' ? 0 : 19)),
      itemType: item.itemType || (item.tvaRate < 1 ? 'debours' : 'honoraire'),
      patientName: item.patientName || '',
      patientIdNumber: item.patientIdNumber || '',
      stockItemId: item.stockItemId || null,
    })) || [{ description: '', qty: 1, price: 0, tax: isMedical ? 7 : 19, itemType: 'honoraire', patientName: '', patientIdNumber: '', stockItemId: null }];
    const totalsRecalculated = calculateTotals(itemsEdit);
    const timbreFiscalEdit = typeof facture.timbreFiscal === 'number' ? facture.timbreFiscal : 1.0;
    setFormData({
      clientId: facture.clientId?._id || facture.clientId || '',
      number: facture.number || '', date: dateFormatted, dueDate: dueDateFormatted,
      items: totalsRecalculated.items,
      subtotal: totalsRecalculated.subtotal, totalTax: totalsRecalculated.totalTax,
      timbreFiscal: timbreFiscalEdit,
      total: totalsRecalculated.subtotal + totalsRecalculated.totalTax + timbreFiscalEdit,
      status: facture.status || 'sent', notes: facture.notes || '',
      isGlobal: facture.isGlobal === true,
    });
    setEditingFacture(facture); setShowModal(true);
  };

  const supprimerFacture = async (factureId) => {
    const facture = factures.find((f) => f._id === factureId);
    if (!canDeleteResource(facture, 'invoice')) { toastError("Vous n'êtes pas autorisé à supprimer cette facture"); return; }
    if (!await showConfirm('Êtes-vous sûr de vouloir supprimer cette facture ?')) return;
    try {
      await invoicesAPI.delete(factureId);
      toastSuccess('Facture supprimée avec succès!');
      await chargerFactures();
    } catch (error) {
      const msg = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Erreur lors de la suppression';
      toastError(msg);
    }
  };

  const handleViewInvoice = (facture) => {
    const client = getClientObject(facture);
    if (client && !canAccessClient(client._id || client.id)) { toastError("Vous n'avez pas accès à cette facture"); return; }
    setViewingFacture(facture); setShowViewModal(true);
  };

  const handleDownloadXML = async (facture) => {
    try {
      const token = localStorage.getItem('token');
      const workspaceId = localStorage.getItem('active_workspace_id');
      const headers = { Authorization: `Bearer ${token}`, 'Accept': 'application/json' };
      if (workspaceId) headers['x-workspace-id'] = workspaceId;
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://app.accountintime.tn'}/api/invoices/${facture._id}/xml`,
        { method: 'GET', headers }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || errorData?.message || 'Erreur lors du téléchargement du XML');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `TEIF_${facture.number || 'facture'}.xml`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
      toastSuccess('Fichier XML TEIF généré avec succès !');
    } catch (error) { toastError(error.message); }
  };

  const handleUpdateFacture = (factureMiseAJour) => {
    setFactures((prev) => prev.map((f) =>
      (f._id || f.id) === (factureMiseAJour._id || factureMiseAJour.id) ? factureMiseAJour : f
    ));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name.includes('total') || name === 'timbreFiscal' ? parseFloat(value) || 0 : value,
    }));
    if (name === 'date') {
      const date = new Date(value);
      date.setMonth(date.getMonth() + 1);
      setFormData((prev) => ({ ...prev, dueDate: date.toISOString().split('T')[0] }));
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    if (field === 'description' || field === 'itemType') newItems[index] = { ...newItems[index], [field]: value };
    else if (field === 'qty') newItems[index] = { ...newItems[index], qty: String(value).replace(/[^0-9]/g, '') };
    else if (field === 'price') newItems[index] = { ...newItems[index], price: sanitizeDecimalTyping(value) };
    else if (field === 'tax') newItems[index] = { ...newItems[index], tax: parseFloat(value) || 0 };
    else if (field === 'stockItemId') {
      newItems[index] = { ...newItems[index], stockItemId: value };
      if (value) {
        const item = stockItems.find(s => (s._id || s.id) === value);
        if (item) {
          newItems[index].description = item.name;
          newItems[index].price = item.sellingPrice || 0;
        }
      }
    }
    else newItems[index] = { ...newItems[index], [field]: value };
    const itemsForCalc = newItems.map((it) => ({ ...it, qty: parseInt(it.qty, 10) || 0, price: parseDecimalFR(it.price) }));
    const finalTotals = calculateTotals(itemsForCalc);
    const timbreFiscal = formData.timbreFiscal ?? 1.0;
    setFormData((prev) => ({
      ...prev, items: newItems,
      subtotal: finalTotals.subtotal, totalTax: finalTotals.totalTax,
      timbreFiscal, total: finalTotals.subtotal + finalTotals.totalTax + timbreFiscal,
    }));
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { description: '', qty: 1, price: 0, tax: isMedical ? 7 : 19, itemType: 'honoraire', patientName: '', patientIdNumber: '', stockItemId: null }],
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      const finalTotals = calculateTotals(newItems);
      const timbreFiscal = formData.timbreFiscal ?? 1.0;
      setFormData((prev) => ({
        ...prev, items: finalTotals.items,
        subtotal: finalTotals.subtotal, totalTax: finalTotals.totalTax,
        timbreFiscal, total: finalTotals.subtotal + finalTotals.totalTax + timbreFiscal,
      }));
    }
  };

  const handleFiltreChange = (e) => {
    const { name, value } = e.target;
    setFiltres((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const resetFiltres = () => { setFiltres({ status: '', clientId: '', dateDebut: '', dateFin: '' }); setPage(1); };

  const getClientObject = (facture) => {
    if (!facture.clientId) return null;
    if (typeof facture.clientId === 'object') return facture.clientId;
    return clients.find((c) => c._id === facture.clientId || c.id === facture.clientId);
  };

  const clientsAccessibles = clients.filter((client) => canAccessClient(client._id || client.id));

  const facturesAutorisees = factures.filter((facture) => {
    if (facture.isGlobal) return true;
    const client = getClientObject(facture);
    if (!client) return false;
    return canAccessClient(client._id || client.id);
  });

  const facturesFiltrees = facturesAutorisees.filter((facture) => {
    if (activeWorkspace) {
      const invoiceWorkspaceId = typeof facture.workspaceId === 'object' ? facture.workspaceId?._id : facture.workspaceId;
      if (invoiceWorkspaceId && invoiceWorkspaceId !== activeWorkspace._id) return false;
    }
    if (filtres.status && facture.status !== filtres.status) return false;
    const factureClientId = facture.clientId && typeof facture.clientId === 'object' ? facture.clientId._id : facture.clientId;
    if (filtres.clientId && String(factureClientId) !== String(filtres.clientId)) return false;
    if (filtres.dateDebut && new Date(facture.date) < new Date(filtres.dateDebut)) return false;
    if (filtres.dateFin && new Date(facture.date) > new Date(filtres.dateFin)) return false;
    return true;
  });

  useEffect(() => { setPage(1); }, [facturesFiltrees.length]);

  const totalPages = Math.ceil(facturesFiltrees.length / PAGE_SIZE) || 1;
  const paginatedFactures = facturesFiltrees.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const canCreateInvoice = hasPermission('invoices', 'create');

  if (authLoading) return <div className="flex-1 p-6"><p className="text-gray-600">Chargement de la session...</p></div>;
  if (loading) return (
    <div className="flex-1 p-6">
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
        <p className="mt-2 text-gray-600">Chargement des factures...</p>
      </div>
    </div>
  );

  return (
    <div className="flex-1 p-6">
      <ToastContainer />

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-1">Facturation</h1>
        <p className="text-gray-500 text-sm">
          Gérez et suivez toutes vos factures
          {activeWorkspace && (
            <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
              {activeWorkspace.name}
            </span>
          )}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-base font-semibold text-gray-800">Gestion des {tenantType === 'cabinet_medical' ? 'Notes d\'honoraires' : 'Factures'}</h3>
          <p className="text-sm text-gray-500">{facturesFiltrees.length} {tenantType === 'cabinet_medical' ? 'note(s)' : 'facture(s)'} trouvée(s)</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canCreateInvoice && (
            <Button variant="primary" size="sm" onClick={() => { resetForm(); setShowModal(true); }}>
              <IconPlus size={15} strokeWidth={1.6} />
              {isMedical ? "Nouvelle note d'honoraire" : 'Nouvelle Facture'}
            </Button>
          )}
          {hasPermission('invoices', 'export') && (
            <Button variant="info" size="sm" onClick={() => setShowExportModal(true)}>
              <IconFileExport size={15} strokeWidth={1.6} />
              Exporter
            </Button>
          )}
        </div>
      </div>

      <InvoiceStats factures={facturesFiltrees} />
      <InvoiceFilters filtres={filtres} clients={clientsAccessibles} handleFiltreChange={handleFiltreChange} resetFiltres={resetFiltres} />
      <InvoicesTable
        facturesFiltrees={paginatedFactures} clients={clients} filtres={filtres}
        editerFacture={editerFacture} supprimerFacture={supprimerFacture}
        onDownloadXML={handleDownloadXML} onViewInvoice={handleViewInvoice}
        canModifyResource={canModifyResource} canDeleteResource={canDeleteResource}
        currentUser={currentUser} hasPermission={hasPermission} onUpdateFacture={handleUpdateFacture}
      />

      {!loading && facturesFiltrees.length > PAGE_SIZE && (
        <div className="flex items-center justify-between px-4 py-3 border border-t-0 border-gray-200 bg-gray-50 rounded-b-xl">
          <span className="text-xs text-gray-500">
            {facturesFiltrees.length} facture(s) — Page {page}/{totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
              className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <IconChevronLeft size={16} strokeWidth={1.6} />
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const start = Math.max(1, Math.min(page - 2, totalPages - 4));
              const p = start + i;
              if (p > totalPages) return null;
              return (
                <button key={p} onClick={() => setPage(p)}
                  className={`min-w-[32px] px-2 py-1 text-xs font-medium rounded-lg border transition-colors ${p === page ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 text-gray-600 hover:bg-white'}`}>
                  {p}
                </button>
              );
            })}
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <IconChevronRight size={16} strokeWidth={1.6} />
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <InvoiceForm
          formData={formData} editingFacture={editingFacture}
          clients={clientsAccessibles} cabinetInfo={cabinetInfo}
          handleSubmit={handleSubmit} handleInputChange={handleInputChange}
          handleItemChange={handleItemChange} addItem={addItem} removeItem={removeItem}
          setShowModal={setShowModal} resetForm={resetForm}
          onToggleGlobal={() => setFormData((prev) => ({ ...prev, isGlobal: !prev.isGlobal }))}
          workspaceStockEnabled={workspaceStockEnabled}
          stockItems={stockItems}
        />
      )}

      {showViewModal && viewingFacture && (
        <InvoiceViewModal
          facture={viewingFacture} client={getClientObject(viewingFacture)}
          cabinetInfo={cabinetInfo} onClose={() => setShowViewModal(false)}
          onUpdateFacture={handleUpdateFacture}
        />
      )}

      {showExportModal && (
        <ExportModal
          show={showExportModal}
          onClose={() => setShowExportModal(false)}
          title="Exporter les factures"
          columns={invoiceExportColumns}
          data={facturesFiltrees}
          filename="factures"
        />
      )}

      <div ref={printRef} style={{ position: 'absolute', left: '-9999px', top: '-9999px' }} />
    </div>
  );
};

export default Invoices;
